import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSessionId, getToken } from "@/utils/storage";
import { getStoredGuestExamUser } from "@/api/exam";
import { ProgressBar } from "@/components/ProgressBar";

const Task3ProcessingScreen = () => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const navigate = useNavigate();

  // IDENTICAL messaging style as the other screens
  const statusMessages = [
    "Reviewing your Task 3 response...",
    "Analyzing fluency and pronunciation...",
    "Evaluating structure and clarity...",
    "Preparing your personalized feedback...",
  ];

  // -----------------------------
  // AUDIO CLEANUP (unchanged)
  // -----------------------------
  useEffect(() => {
    (async () => {
      try {
        const audioRef = (window as any).audioServiceRef?.current;
        if (audioRef) {
          audioRef.stopRecording?.();
          audioRef.cancelAIAudio?.();
          audioRef.cleanup?.();
        }
      } catch {}

      try {
        if (navigator?.mediaDevices?.getUserMedia) {
          const s = await navigator.mediaDevices.getUserMedia({ audio: true });
          s.getTracks().forEach((t) => {
            try {
              (MediaStreamTrack.prototype as any).stop.call(t);
            } catch {
              try {
                t.stop();
              } catch {}
            }
          });
        }
      } catch {}

      try {
        const ctx: AudioContext | undefined = (window as any).__appAudioContext;
        if (ctx?.close) {
          await ctx.close();
          (window as any).__appAudioContext = undefined;
        }
      } catch {}
    })();
  }, []);

  // -----------------------------
  // STATUS MESSAGE ROTATION
  // -----------------------------
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % statusMessages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [statusMessages.length]);

  // -----------------------------
  // TASK 3 PROCESSING LOGIC (unchanged)
  // -----------------------------
  useEffect(() => {
    const processTask3 = async () => {
      try {
        const sessionIdFromStorage = getSessionId();
        const currentExamId = localStorage.getItem("current_exam_id");
        const sessionId = currentExamId || sessionIdFromStorage;

        const paidToken = getToken();
        const guestUser = getStoredGuestExamUser();
        const guestToken =
          guestUser?.token ?? localStorage.getItem("guestToken");

        const isGuest = !paidToken && !!guestToken;
        const token = paidToken || guestToken;

        if (!sessionId || !token) {
          setIsProcessing(false);
          setIsComplete(true);
          return;
        }

        const attemptId = localStorage.getItem("current_task_attempt_id");
        const topicId = localStorage.getItem("task3_topic_id");

        const payload: any = { session_id: sessionId };
        if (attemptId) payload.task_attempt_id = attemptId;
        if (topicId) payload.topic_id = topicId;
        if (isGuest) {
          payload.guest_id = guestUser?.user_id;
          payload.is_guest = true;
        }

        const response = await fetch(
          "https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/task3-individual-new",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          const result = await response.json();
          if (result?.analysis_data) {
            localStorage.setItem(
              "latest_analysis_data",
              JSON.stringify(result.analysis_data)
            );
          }
        }

        await new Promise((res) => setTimeout(res, 1500));
        setIsProcessing(false);
        setIsComplete(true);
      } catch (error) {
        console.error("Task 3 retry error:", error);
        setIsProcessing(false);
        setIsComplete(true);
      }
    };

    processTask3();
  }, []);

  // -----------------------------
  // NAVIGATE ONLY WHEN COMPLETE
  // -----------------------------
  useEffect(() => {
    if (!isComplete) return;

    const timeout = setTimeout(() => {
      navigate("/single-task-feedback");
    }, 400);

    return () => clearTimeout(timeout);
  }, [isComplete, navigate]);

  // -----------------------------
  // FINAL UI — IDENTICAL TO T1/T2/COMBINED
  // -----------------------------
  return (
    <div
      className="
        flex flex-col items-center justify-center
        relative overflow-hidden
        h-full
        px-4 py-12
        md:px-6 md:py-16
      "
    >
      <div className="flex flex-col items-center text-center max-w-2xl mx-auto">

        {/* Spinner Icon (same across all screens) */}
        <div className="relative mb-6">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-primary/10 flex items-center justify-center">
            <div className="w-10 h-10 md:w-12 md:h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          Preparing Your Task 3 Results
        </h1>

        {/* Status Message */}
        <div className="space-y-3 mb-6">
          <p className="text-lg md:text-xl text-muted-foreground">
            {statusMessages[currentMessageIndex]}
          </p>
        </div>

        {/* Progress Bar */}
        <ProgressBar isComplete={isComplete} />

        {/* Bottom Info (identical wording across screens) */}
        <div className="mt-8 text-sm text-muted-foreground max-w-xl mx-auto">
          <p>
            This usually takes less than a minute. Please keep this tab open so your
            results and next-step tips can load.
          </p>
        </div>

      </div>
    </div>
  );
};

export default Task3ProcessingScreen;
